<template>
  <li>
    <LargePicker
      :addon="option.addon"
      :color="option.color"
      :disabled="shouldDisplayDisabledState"
      :index
      :is-good-deal="option.goodDeal"
      :is-small
      :label
      label-text-token="body-2"
      :loading
      :prefix
      :price="description"
      :selected="option.selected"
      :sub-label
      :tag
      @click="onClick"
    />
  </li>
</template>

<script lang="ts" setup>
import { navigateTo } from '#imports'
import { computed, ref, watch } from 'vue'

import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { useTracking } from '@backmarket/nuxt-module-tracking/useTracking'

import LargePicker from '~/scopes/product/components/CustomizationFunnel/components/LargePicker/LargePicker.vue'
import { useCreateLinkFromPicker } from '~/scopes/product/composables/useCreateLinkFromPicker'

import type { Step } from '../../utils/types'

import translations from './StepOption.translations'

const { trackClick } = useTracking()
const i18n = useI18n()
const { createLink } = useCreateLinkFromPicker()
const loading = ref(false)

const props = withDefaults(
  defineProps<{
    step: Step
    option: Step['options'][number]
    index: number
    isGroupLoading?: boolean
    isSmall?: boolean
    shouldTriggerNavigation?: boolean
  }>(),
  {
    isGroupLoading: false,
    isSmall: false,
    shouldTriggerNavigation: true,
  },
)

const emit = defineEmits<{
  click: [Step['options'][number], Step]
  changed: [Step['options'][number], Step]
}>()

// When we click on a picker, the `loading` state is added manually. Behind the scene, we're triggering
// a /pickers endpoint call, so let's remove the loading state once the picker status change to selected
// (as it's backend driven)
watch(
  () => props.option.selected,
  () => {
    if (loading.value && props.option.selected) {
      emit('changed', props.option, props.step)
      loading.value = false
    }
  },
)

const prefix = computed(() => {
  return props.option.style?.prefix && props.option.style?.prefix !== 'circle'
    ? props.option.style.prefix
    : undefined
})

const description = computed(() => {
  if (props.step.id === 'battery' && !props.option.acquirable) {
    return ''
  }

  if (props.step.id === 'battery' && !props.option.available) {
    return i18n(translations.comingSoon)
  }

  if (!props.option.available && props.step.id !== 'mobile_plan') {
    return i18n(translations.soldOut)
  }

  if (!props.option.price) {
    return ''
  }

  // For trade-in, the price is an already formatted string :'(
  if (typeof props.option.price === 'string') {
    return props.option.price
  }

  return i18n.price(props.option.price)
})

const label = computed(() => {
  return typeof props.option.label === 'string'
    ? props.option.label
    : i18n(props.option.label)
})

const subLabel = computed(() => {
  return typeof props.option.subLabel === 'object'
    ? i18n(props.option.subLabel)
    : props.option.subLabel
})

const tag = computed(() => {
  if (props.option.tag) {
    return {
      label: props.option.tag.label,
      variant: props.option.tag.type,
    }
  }

  return ''
})

const shouldDisplayDisabledState = computed(() => {
  return !props.option.available || (!loading.value && props.isGroupLoading)
})

async function onClick() {
  if (!props.option.available || loading.value || props.isGroupLoading) {
    return false
  }

  if (props.step.id === 'battery') {
    // We have an exception on new battery for now, to be compatible with the legacy event we have on the
    // toggle in the previous pickers.
    trackClick({
      zone: props.step.trackingId ?? '',
      name: props.step.trackingId ?? 'unknown_picker',
      value: props.option.trackingValue ?? '',
    })
  } else {
    // We're a bit defensive on those trackingId / trackingValue, as the API specs says that
    // both fields are optional. Let's have default values to make sure we're able to detect this
    // in amplitude no matter what
    trackClick({
      zone: props.step.trackingId ?? 'unknown_picker',
      name: props.option.trackingValue ?? label.value,
    })
  }

  emit('click', props.option, props.step)

  // If the field is already selected, we won't change the URL nor refresh the network calls, so
  // let's directly trigger the changed event and skip the loading state
  if (props.option.selected) {
    emit('changed', props.option, props.step)
  } else if (props.shouldTriggerNavigation) {
    loading.value = true
  }

  // Update current URL
  const link = createLink(props.option, props.step)

  if (link && props.shouldTriggerNavigation) {
    await navigateTo(link)
  }

  return true
}
</script>
